/**
 * Named wave-print recipes for Printing Toolkit / Packers.
 * Local to the workstation — not a template designer.
 *
 * format:
 *  - auto — Assemble day stack picks Standard vs Cheetah vs GO4 per order (method + history)
 *  - standard / cheetah / go4 — lock every slip in the wave to that paper
 */

export const PRINT_WAVE_RECIPE_KEY = 'lsc.printWaveRecipes.v1';

export const DEFAULT_PRINT_WAVE_RECIPES = Object.freeze([
  Object.freeze({
    id: 'friday-full-day',
    name: 'Friday full day (auto paper + inscriptions + carriers)',
    format: 'auto',
    includePickList: true,
    includeInscriptions: true,
    includeSeparators: true,
    includeCarrierLabels: true,
    showPrices: false,
    showSignatureLines: true,
  }),
  Object.freeze({
    id: 'friday-standard',
    name: 'Friday Standard wave',
    format: 'standard',
    includePickList: true,
    includeInscriptions: true,
    includeSeparators: true,
    includeCarrierLabels: false,
    showPrices: false,
    showSignatureLines: true,
  }),
  Object.freeze({
    id: 'cheetah-run',
    name: 'Cheetah LZ17 wave',
    format: 'cheetah',
    includePickList: true,
    includeInscriptions: true,
    includeSeparators: true,
    includeCarrierLabels: false,
    showPrices: false,
    showSignatureLines: true,
  }),
  Object.freeze({
    id: 'retail-standard-quiet',
    name: 'Retail Standard (no prices)',
    format: 'standard',
    includePickList: true,
    includeInscriptions: false,
    includeSeparators: true,
    includeCarrierLabels: false,
    showPrices: false,
    showSignatureLines: true,
  }),
]);

const RECIPE_IDS = new Set(DEFAULT_PRINT_WAVE_RECIPES.map((recipe) => recipe.id));

export function createDefaultPrintWaveRecipeState() {
  return {
    version: 1,
    activeRecipeId: 'friday-full-day',
    recipes: DEFAULT_PRINT_WAVE_RECIPES.map((recipe) => ({ ...recipe })),
    updatedAt: new Date().toISOString(),
  };
}

export function sanitizePrintWaveRecipeFormat(value) {
  if (value === 'cheetah' || value === 'go4' || value === 'auto') return value;
  return 'standard';
}

export function sanitizePrintWaveRecipe(value) {
  const fallback = DEFAULT_PRINT_WAVE_RECIPES[0];
  const input = value && typeof value === 'object' ? value : {};
  const id = RECIPE_IDS.has(input.id) ? input.id : `custom-${String(input.id || 'recipe').slice(0, 24)}`;
  return {
    id,
    name: String(input.name || fallback.name).trim() || fallback.name,
    format: sanitizePrintWaveRecipeFormat(input.format),
    includePickList: input.includePickList !== false,
    includeInscriptions: input.includeInscriptions === true,
    includeSeparators: input.includeSeparators !== false,
    includeCarrierLabels: input.includeCarrierLabels === true,
    showPrices: input.showPrices === true,
    showSignatureLines: input.showSignatureLines !== false,
  };
}

export function sanitizePrintWaveRecipeState(value) {
  const base = createDefaultPrintWaveRecipeState();
  if (!value || typeof value !== 'object') return base;
  const savedRecipes =
    Array.isArray(value.recipes) && value.recipes.length
      ? value.recipes.map(sanitizePrintWaveRecipe)
      : [];
  const byId = new Map(savedRecipes.map((recipe) => [recipe.id, recipe]));
  // Keep operator custom recipes, and ensure built-in defaults (e.g. friday-full-day) exist.
  DEFAULT_PRINT_WAVE_RECIPES.forEach((recipe) => {
    const existing = byId.get(recipe.id);
    if (!existing) {
      byId.set(recipe.id, { ...recipe });
      return;
    }
    // Refresh built-in name; migrate legacy friday-full-day standard lock → auto paper.
    byId.set(recipe.id, {
      ...recipe,
      ...existing,
      name: recipe.name,
      format:
        recipe.id === 'friday-full-day' && existing.format === 'standard'
          ? 'auto'
          : sanitizePrintWaveRecipeFormat(existing.format ?? recipe.format),
    });
  });
  const recipes = [
    ...DEFAULT_PRINT_WAVE_RECIPES.map((recipe) => byId.get(recipe.id)),
    ...savedRecipes.filter((recipe) => !RECIPE_IDS.has(recipe.id)),
  ];
  const preferredActive =
    value.activeRecipeId ||
    (byId.has('friday-full-day') ? 'friday-full-day' : recipes[0]?.id);
  const activeRecipeId = recipes.some((recipe) => recipe.id === preferredActive)
    ? preferredActive
    : recipes[0]?.id || base.activeRecipeId;
  return {
    version: 1,
    activeRecipeId,
    recipes,
    updatedAt: value.updatedAt || new Date().toISOString(),
  };
}

export function loadPrintWaveRecipeState() {
  try {
    const raw = localStorage.getItem(PRINT_WAVE_RECIPE_KEY);
    if (!raw) return createDefaultPrintWaveRecipeState();
    return sanitizePrintWaveRecipeState(JSON.parse(raw));
  } catch {
    return createDefaultPrintWaveRecipeState();
  }
}

export function savePrintWaveRecipeState(next) {
  const sanitized = sanitizePrintWaveRecipeState({
    ...next,
    updatedAt: new Date().toISOString(),
  });
  try {
    localStorage.setItem(PRINT_WAVE_RECIPE_KEY, JSON.stringify(sanitized));
  } catch {
    /* ignore quota */
  }
  return sanitized;
}

export function getActivePrintWaveRecipe(state = loadPrintWaveRecipeState()) {
  const active =
    state.recipes.find((recipe) => recipe.id === state.activeRecipeId) ||
    state.recipes[0] ||
    DEFAULT_PRINT_WAVE_RECIPES[0];
  return { ...active };
}

export function patchActivePrintWaveRecipe(patch) {
  const state = loadPrintWaveRecipeState();
  const recipes = state.recipes.map((recipe) =>
    recipe.id === state.activeRecipeId
      ? sanitizePrintWaveRecipe({ ...recipe, ...patch, id: recipe.id })
      : recipe,
  );
  return savePrintWaveRecipeState({ ...state, recipes });
}
